import { IonButton, IonInput } from '@ionic/react';
import React, { KeyboardEvent, useCallback, useRef, useState } from 'react';

import {
  ButtonContainer,
  Spacer,
  SuggestedMobileNumbers
} from '@/components/common';
import { Icon } from '@/components/memo';
import { useAuth, useSignIn } from '@/contexts';
import { useInputFocus, useToast } from '@/hooks';
import { MOBILE_NUMBER_LENGTH } from '@/utils/constants';
import { getUserMobileNumber } from '@/utils/helpers/getUserMobileNumber';
import { handleIonMobileNumberInput } from '@/utils/helpers/sanitizeMobileNumberInput';
import { arrowForwardCircleOutline } from 'ionicons/icons';

import './MobileNumber.css';

const MobileNumber: React.FC = () => {
  const { user } = useAuth();
  const { mobileNumber, setMobileNumber, sendOtp } = useSignIn();
  const displayToast = useToast();
  const [hasError, setHasError] = useState<boolean>(false);
  const inputRef = useRef<HTMLIonInputElement>(null);

  useInputFocus(inputRef);

  const copy = {
    subHeader: 'ENTER YOUR MOBILE NUMBER',
    header: (
      <>
        What's your <span>mobile</span> number?
      </>
    ),
    message: 'An OTP will be sent to your mobile number for verification.',
    buttonText: 'Next',
    countryCode: '+63'
  };

  const onInput = (event: CustomEvent) => {
    handleIonMobileNumberInput(event, (sanitized) => {
      setMobileNumber(sanitized);
      setHasError(false);
    });
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      onNext();
    }
  };

  const onNext = useCallback(() => {
    if (user && mobileNumber === getUserMobileNumber(user)) {
      setHasError(true);
      displayToast({
        message:
          'You are already signed in with this mobile number. Please use a different mobile number.',
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'next-button'
      });
      return;
    }

    if (
      mobileNumber &&
      mobileNumber.length === MOBILE_NUMBER_LENGTH &&
      /^\d+$/.test(mobileNumber)
    ) {
      setHasError(false);
      sendOtp();
    } else {
      setHasError(true);
      displayToast({
        message: 'Please enter a valid mobile number.',
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'next-button'
      });
    }
  }, [user, mobileNumber, sendOtp]);

  const onSelectSavedNumber = useCallback(
    (selectedMobileNumber: string) => {
      if (selectedMobileNumber === mobileNumber) {
        setMobileNumber('');
        setHasError(false);
        return;
      }
      setMobileNumber(selectedMobileNumber);
      setHasError(false);
    },
    [mobileNumber]
  );

  return (
    <div className="sign-in-mobile-number">
      <Spacer top={32} bottom={24}>
        <h4 className="sign-in-mobile-number__sub-header">{copy.subHeader}</h4>
        <h1 className="sign-in-mobile-number__header">{copy.header}</h1>
        <p className="sign-in-mobile-number__message">{copy.message}</p>
      </Spacer>

      <Spacer bottom={102}>
        <div className="sign-in-mobile-number__form">
          <div
            className={`sign-in-mobile-number__input ${hasError ? 'sign-in-mobile-number__input--error' : ''}`}
          >
            <IonInput
              ref={inputRef}
              type="tel"
              label="Mobile Number"
              labelPlacement="stacked"
              value={mobileNumber}
              placeholder="9---------"
              onIonInput={onInput}
              onKeyDown={onKeyDown}
              maxlength={10}
            >
              <div slot="start">{copy.countryCode}</div>
            </IonInput>
          </div>

          <SuggestedMobileNumbers
            currentMobileNumber={mobileNumber || ''}
            onSelectMobileNumber={onSelectSavedNumber}
          />
        </div>
      </Spacer>
      <ButtonContainer>
        <IonButton
          id="next-button"
          onClick={onNext}
          expand="block"
          fill="solid"
          size="default"
          color="primary"
          strong={true}
        >
          <Icon slot="start" icon={arrowForwardCircleOutline} />
          {copy.buttonText}
        </IonButton>
      </ButtonContainer>
    </div>
  );
};

export default MobileNumber;
